import styled, { css } from "styled-components";

interface IPoweredByPillProps {
  /** When set, the pill renders as a button that triggers this handler. */
  onClick?: () => void;
  /** Accessible label for the interactive variant. */
  ariaLabel?: string;
  /** Renders in the document flow instead of pinned to the bottom-right. */
  inline?: boolean;
  className?: string;
}

const Pill = styled.div<{ $interactive: boolean; $inline: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;

  height: var(--powered-by-badge-height);
  padding: 0 24px;
  border: none;
  border-radius: 999px;

  background: var(--brand-white);
  box-shadow: 0 8px 24px rgba(23, 20, 63, 0.12);

  user-select: none;

  ${({ $inline }) =>
    !$inline &&
    css`
      position: fixed;
      right: var(--powered-by-badge-offset);
      bottom: var(--powered-by-badge-offset);
      z-index: 900;
    `}

  ${({ $interactive }) =>
    $interactive
      ? css`
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;

          :hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 28px rgba(23, 20, 63, 0.18);
          }
        `
      : css`
          pointer-events: none;
        `}

  @media (max-width: 720px) {
    padding: 0 16px;
    gap: 8px;
  }
`;

const Label = styled.span`
  font-size: 1rem;
  font-weight: 500;
  white-space: nowrap;
  color: var(--brand-gray-600);

  @media (max-width: 720px) {
    font-size: 0.875rem;
  }
`;

const Logo = styled.img`
  height: 28px;
  object-fit: contain;

  @media (max-width: 720px) {
    height: 22px;
  }
`;

/**
 * The "Powered by Clear Health" pill. Static by default; pass `onClick` to get
 * the interactive variant used as the chat widget's collapsed button.
 */
export function PoweredByPill({
  onClick,
  ariaLabel,
  inline = false,
  className,
}: IPoweredByPillProps): JSX.Element {
  const interactive = !!onClick;

  return (
    <Pill
      className={className ? `powered-by-pill ${className}` : "powered-by-pill"}
      $interactive={interactive}
      $inline={inline}
      {...(interactive
        ? { as: "button", type: "button", onClick, "aria-label": ariaLabel }
        : {})}
    >
      <Label>Powered by</Label>
      <Logo src="/logo_new.svg" alt="Clear Health" />
    </Pill>
  );
}
